import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../services/api';
import { 
  products as initialProducts, 
  defaultCategories,
  defaultMasterRates,
  liveGoldRates as defaultRates,
  defaultCoupons,
  defaultStoreSettings,
  defaultHeroBanner,
  defaultAnnouncements,
  defaultStories,
  defaultTestimonials,
  defaultTrustPillars,
  defaultPromoPopup
} from '../data/products';

const ShopContext = createContext();

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

// ============================================================================
// MASTER PRICING ENGINE:
// Formula:
//   Metal Value = Product Weight (g) * Current Master Rate Per Gram
//   Making Charge = Metal Value * (Making Charge % / 100)
//   Final Product Price = Metal Value + Making Charge
// ============================================================================

export const getMasterRateForProduct = (product, masterRates = defaultMasterRates) => {
  try {
    if (!product || typeof product !== 'object') return 6765;
    if (product.category === 'kitchenware') return 0;
    const rates = (masterRates && typeof masterRates === 'object') ? masterRates : defaultMasterRates;

    const isSilver = 
      product.metalType === 'Silver' ||
      product.category === 'silverware' || 
      (typeof product.metal === 'string' && product.metal.toLowerCase().includes('silver')) ||
      (typeof product.purity === 'string' && product.purity.toLowerCase().includes('silver'));

    const rawList = isSilver ? rates?.silver : rates?.gold;
    const fallbackList = isSilver ? defaultMasterRates.silver : defaultMasterRates.gold;
    const metalList = Array.isArray(rawList) ? rawList : fallbackList;

    const purityClean = (product.purity || product.goldPurity || product.metal || '').toString().trim().toUpperCase();

    // Check explicit product-level rate override first
    if (Number(product.ratePerGram) > 0) return Number(product.ratePerGram);
    if (Number(product.metalRate) > 0) return Number(product.metalRate);

    // 1. Exact match by label
    const exact = metalList.find(m => m?.label && m.label.toUpperCase() === purityClean);
    if (exact && Number(exact.rate) > 0) return Number(exact.rate);

    // 2. Exact match by id
    const idMatch = metalList.find(m => m?.id && m.id.toUpperCase() === purityClean);
    if (idMatch && Number(idMatch.rate) > 0) return Number(idMatch.rate);

    // 3. Partial match by label or id
    const partial = metalList.find(m => {
      const lbl = (m?.label || '').toUpperCase();
      const id = (m?.id || '').toUpperCase();
      return (
        (purityClean && lbl && (purityClean.includes(lbl) || lbl.includes(purityClean))) ||
        (purityClean && id && (purityClean.includes(id) || id.includes(purityClean)))
      );
    });
    if (partial && Number(partial.rate) > 0) return Number(partial.rate);

    // 4. Fallbacks
    if (isSilver) {
      // Check numeric matches in purityClean (e.g. "800", "70", "925", "999")
      const numMatch = metalList.find(m => {
        const numbersInPurity = purityClean.match(/\d+/g);
        if (!numbersInPurity) return false;
        const mStr = `${m?.label || ''} ${m?.id || ''}`;
        return numbersInPurity.some(num => mStr.includes(num));
      });
      if (numMatch && Number(numMatch.rate) > 0) return Number(numMatch.rate);

      if (purityClean.includes('925')) {
        const s925 = metalList.find(m => m?.label && m.label.includes('925'));
        if (s925 && Number(s925.rate) > 0) return Number(s925.rate);
      }
      const s999 = metalList.find(m => m?.label && m.label.includes('999'));
      if (s999 && Number(s999.rate) > 0) return Number(s999.rate);

      if (metalList.length > 0 && Number(metalList[0]?.rate) > 0) {
        return Number(metalList[0].rate);
      }
      return 89.5;
    }

    if (purityClean.includes('24') || purityClean.includes('999')) {
      const g24 = metalList.find(m => m?.label && m.label.includes('24'));
      if (g24 && Number(g24.rate) > 0) return Number(g24.rate);
      return 7380;
    }
    if (purityClean.includes('18') || purityClean.includes('750')) {
      const g18 = metalList.find(m => m?.label && m.label.includes('18'));
      if (g18 && Number(g18.rate) > 0) return Number(g18.rate);
      return 5535;
    }
    if (purityClean.includes('14') || purityClean.includes('585')) {
      const g14 = metalList.find(m => m?.label && m.label.includes('14'));
      if (g14 && Number(g14.rate) > 0) return Number(g14.rate);
      return 4310;
    }

    const g22 = metalList.find(m => m?.label && m.label.includes('22'));
    if (g22 && Number(g22.rate) > 0) return Number(g22.rate);
    return 6765;
  } catch (err) {
    console.error('Error in getMasterRateForProduct:', err);
    return 6765;
  }
};

export const calculateProductPrice = (product, masterRates = defaultMasterRates) => {
  if (!product || typeof product !== 'object') {
    return { 
      finalPrice: 0, 
      taxableAmount: 0, 
      gstAmount: 0, 
      metalValue: 0, 
      diamondAmount: 0,
      gemstoneAmount: 0,
      makingCharge: 0, 
      ratePerGram: 0, 
      weight: 0, 
      makingChargePercent: 0,
      metalType: 'Gold',
      purity: '22K'
    };
  }

  try {
    if (product.category === 'kitchenware') {
      const fixedVal = Number(product.price) || Number(product.customPrice) || 18500;
      const preTax = Math.round(fixedVal / 1.03);
      const manualGst = fixedVal - preTax;
      return {
        finalPrice: fixedVal,
        taxableAmount: preTax,
        gstAmount: manualGst,
        metalValue: preTax,
        diamondAmount: 0,
        gemstoneAmount: 0,
        makingCharge: 0,
        makingChargePercent: 0,
        ratePerGram: 0,
        weight: product.grossWeight || 'Set',
        metalType: product.metal || 'Food-Grade Stainless Steel / Copper',
        purity: product.purity || 'Food Grade Certified'
      };
    }

    const isManual = product.pricingMode === 'manual' || 
                     product.pricing_mode === 'manual';

    // Resolve rate per gram
    const ratePerGram = getMasterRateForProduct(product, masterRates);

    // Resolve weight in grams (support goldWeight, netGoldWeight, grossWeight)
    let rawWeight = product.netGoldWeight || product.goldWeight || product.weight || product.grossWeight;
    const weight = parseFloat(rawWeight) || 1.0;

    // Resolve making charge percent (Default 6.99% or custom)
    let rawMaking = product.makingChargePercent;
    if (rawMaking === undefined || rawMaking === null || rawMaking === '') {
      rawMaking = product.makingDiscountPercent ? parseFloat(product.makingDiscountPercent) : 6.99;
    }
    const makingChargePercent = parseFloat(rawMaking) || 6.99;

    if (isManual && (Number(product.price) > 0 || Number(product.customPrice) > 0)) {
      const manualVal = Number(product.price) || Number(product.customPrice) || 0;
      const preTax = Math.round(manualVal / 1.03);
      const manualGst = manualVal - preTax;

      let manualMetal = (weight > 0 && ratePerGram > 0) ? Math.round(weight * ratePerGram) : Math.round(preTax * 0.90);
      if (manualMetal >= preTax) {
        manualMetal = Math.round(preTax * (100 / (100 + makingChargePercent)));
      }
      const manualMaking = Math.max(0, preTax - manualMetal);

      return {
        finalPrice: manualVal,
        taxableAmount: preTax,
        gstAmount: manualGst,
        metalValue: manualMetal,
        diamondAmount: 0,
        gemstoneAmount: 0,
        makingCharge: manualMaking,
        makingChargePercent: makingChargePercent,
        ratePerGram: ratePerGram,
        weight: weight,
        metalType: product.metalType || 'Gold',
        purity: product.purity || '18KT (750)'
      };
    }

    // Check if product has explicit priceBreakup
    let pb = product.priceBreakup || product.price_breakup;
    if (typeof pb === 'string') {
      try { pb = JSON.parse(pb); } catch (_) { pb = null; }
    }

    const pbGold = Number(pb?.goldAmount || pb?.metalValue) || 0;
    const pbMaking = Number(pb?.makingChargesDiscounted || pb?.makingCharge) || 0;

    // Check if product has diamonds
    const caratLower = String(product.diamondCarat || '').toLowerCase();
    const hasDiamond = Boolean(
      (pb && Number(pb.diamondAmount) > 0) ||
      (product.diamondAmount && Number(product.diamondAmount) > 0) ||
      (product.diamondCarat && 
        !caratLower.includes('n/a') && 
        !caratLower.includes('pure gold') && 
        !caratLower.includes('none'))
    );

    let metalValue = 0;
    let diamondAmount = 0;
    let gemstoneAmount = 0;
    let makingCharge = 0;
    let taxableAmount = 0;
    let gstAmount = 0;
    let finalPrice = 0;

    // 1. Explicit priceBreakup configured
    if (pb && (Number(pb.diamondAmount) > 0 || pbGold > 0 || pbMaking > 0 || Number(pb.finalTotal) > 0)) {
      diamondAmount = Number(pb.diamondAmount) || 0;
      gemstoneAmount = Number(pb.gemstoneAmount) || 0;

      if (product.pricingMode === 'auto') {
        metalValue = pbGold > 0 ? pbGold : Math.round(weight * ratePerGram);
        makingCharge = pbMaking > 0 ? pbMaking : Math.round(metalValue * (makingChargePercent / 100));
      } else {
        const baseRate = getMasterRateForProduct(product, defaultMasterRates);
        const rateRatio = (baseRate > 0 && ratePerGram > 0) ? (ratePerGram / baseRate) : 1;
        metalValue = Math.round((pbGold || (weight * ratePerGram)) * rateRatio);
        makingCharge = pbMaking || Math.round(metalValue * (makingChargePercent / 100));
      }

      taxableAmount = metalValue + diamondAmount + gemstoneAmount + makingCharge;
      gstAmount = Math.round(taxableAmount * 0.03);
      finalPrice = taxableAmount + gstAmount;
    }
    // 2. Preset catalog product price (e.g. studded jewellery ml-01, ml-02, ml-03, ml-06, etc.)
    else if (Number(product.price) > 0 && product.pricingMode !== 'auto') {
      const catalogPrice = Number(product.price);
      const preTax = Math.round(catalogPrice / 1.03);
      
      const currentMetal = Math.round(weight * ratePerGram);
      const baseRate = getMasterRateForProduct(product, defaultMasterRates);
      const baseMetal = Math.round(weight * baseRate);

      metalValue = currentMetal;
      makingCharge = Math.round(metalValue * (makingChargePercent / 100));
      const baseMaking = Math.round(baseMetal * (makingChargePercent / 100));

      if (hasDiamond) {
        // Remaining pre-tax value after base metal and making is diamond value
        diamondAmount = Math.max(0, preTax - baseMetal - baseMaking);
      } else if (preTax > (baseMetal + baseMaking)) {
        gemstoneAmount = preTax - baseMetal - baseMaking;
      }

      taxableAmount = metalValue + diamondAmount + gemstoneAmount + makingCharge;
      gstAmount = Math.round(taxableAmount * 0.03);
      finalPrice = taxableAmount + gstAmount;
    }
    // 3. Auto pricing mode (pure bullion weight-based calculation)
    else {
      metalValue = Math.round(weight * ratePerGram);
      makingCharge = Math.round(metalValue * (makingChargePercent / 100));
      diamondAmount = Number(product.diamondAmount) || 0;
      gemstoneAmount = Number(product.gemstoneAmount) || 0;

      taxableAmount = metalValue + diamondAmount + gemstoneAmount + makingCharge;
      gstAmount = Math.round(taxableAmount * 0.03);
      finalPrice = taxableAmount + gstAmount;
    }

    return {
      finalPrice,
      taxableAmount,
      gstAmount,
      metalValue,
      diamondAmount,
      gemstoneAmount,
      makingCharge,
      makingChargePercent,
      ratePerGram,
      weight,
      metalType: product.metalType || 'Gold',
      purity: product.purity || product.goldPurity || '22K'
    };
  } catch (err) {
    console.error('Error in calculateProductPrice:', err);
    const fallbackPrice = Number(product?.price) || 0;
    const fallbackTaxable = Math.round(fallbackPrice / 1.03);
    return {
      finalPrice: fallbackPrice,
      taxableAmount: fallbackTaxable,
      gstAmount: fallbackPrice - fallbackTaxable,
      metalValue: fallbackTaxable,
      diamondAmount: 0,
      gemstoneAmount: 0,
      makingCharge: 0,
      makingChargePercent: 0,
      ratePerGram: 0,
      weight: 0,
      metalType: 'Gold',
      purity: '22K'
    };
  }
};

export const ShopProvider = ({ children }) => {
  // Admin Portal View & Authentication
  const [isAdminMode, setIsAdminModeState] = useState(() => {
    try {
      return typeof window !== 'undefined' && (window.location.hash === '#admin' || window.location.hash.startsWith('#/admin'));
    } catch {
      return false;
    }
  });

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => {
    try {
      return sessionStorage.getItem('ml_admin_auth') === 'true';
    } catch {
      return false;
    }
  });

  // Explicit helper to toggle or navigate to/from Admin without breaking storefront routes
  const setIsAdminMode = useCallback((val) => {
    const shouldBeAdmin = Boolean(val);
    setIsAdminModeState(shouldBeAdmin);
    if (typeof window !== 'undefined') {
      if (shouldBeAdmin) {
        if (window.location.hash !== '#/admin' && window.location.hash !== '#admin') {
          window.location.hash = '#/admin';
        }
      } else {
        if (window.location.hash === '#admin' || window.location.hash.startsWith('#/admin')) {
          window.location.hash = '#/';
        }
      }
    }
  }, []);

  // Sync admin mode state with browser hash navigation without hijacking storefront URLs
  useEffect(() => {
    const handleHash = () => {
      if (typeof window !== 'undefined') {
        const isCurrentAdmin = window.location.hash === '#admin' || window.location.hash.startsWith('#/admin');
        setIsAdminModeState(isCurrentAdmin);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  // Store Settings (Brand info, Tax GSTIN, UPI, Admin Security)
  const [storeSettings, setStoreSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_settings_v1');
      return saved ? { ...defaultStoreSettings, ...JSON.parse(saved) } : defaultStoreSettings;
    } catch {
      return defaultStoreSettings;
    }
  });

  // ==================== CUSTOMER AUTH & DASHBOARD STATE ====================
  const [currentCustomer, setCurrentCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_customer_v1');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const isCustomerLoggedIn = Boolean(currentCustomer?.phone);
  const [isCustomerAuthModalOpen, setIsCustomerAuthModalOpen] = useState(false);
  const [customerAuthMode, setCustomerAuthMode] = useState('login'); // 'login' or 'signup'

  const openCustomerAuthModal = (mode = 'login') => {
    setCustomerAuthMode(mode);
    setIsCustomerAuthModalOpen(true);
  };

  const closeCustomerAuthModal = () => {
    setIsCustomerAuthModalOpen(false);
  };

  const [savedAddresses, setSavedAddresses] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_customer_addresses_v1');
      return saved ? JSON.parse(saved) : [
        {
          id: 'addr-default',
          title: 'Primary Residence',
          fullName: 'Ayush Sharma',
          phone: '9829012345',
          pincode: '302002',
          address: 'Plot 42, Sarafa Heritage Enclave, Near Bada Chauraha',
          city: 'Jaipur',
          state: 'Rajasthan',
          landmark: 'Opposite State Bank',
          isDefault: true
        }
      ];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('ml_customer_addresses_v1', JSON.stringify(savedAddresses));
    } catch (e) { console.error(e); }
  }, [savedAddresses]);

  const addSavedAddress = (newAddr) => {
    const addr = {
      id: `addr-${Date.now()}`,
      ...newAddr,
      isDefault: savedAddresses.length === 0 || Boolean(newAddr.isDefault)
    };
    setSavedAddresses(prev => {
      let list = newAddr.isDefault ? prev.map(a => ({ ...a, isDefault: false })) : [...prev];
      return [addr, ...list];
    });
    addToast('New delivery address saved!', 'success');
  };

  const deleteSavedAddress = (id) => {
    setSavedAddresses(prev => prev.filter(a => a.id !== id));
    addToast('Address removed', 'info');
  };

  const signupCustomer = async (data) => {
    try {
      const res = await api.signupCustomer(data);
      if (res.success && res.customer) {
        setCurrentCustomer(res.customer);
        try {
          localStorage.setItem('ml_customer_v1', JSON.stringify(res.customer));
          if (res.token) localStorage.setItem('ml_customer_token', res.token);
        } catch (e) { console.error(e); }
        setIsCustomerAuthModalOpen(false);
        addToast(`Welcome to M.L. Jewellers, ${res.customer.name}!`, 'success');
        return { success: true, customer: res.customer };
      } else {
        addToast(res.error || 'Failed to register account', 'error');
        return { success: false, error: res.error };
      }
    } catch (err) {
      addToast(err.message || 'Signup failed', 'error');
      return { success: false, error: err.message };
    }
  };

  const loginCustomer = async (phone, pin) => {
    try {
      const res = await api.loginCustomer(phone, pin);
      if (res.success && res.customer) {
        setCurrentCustomer(res.customer);
        try {
          localStorage.setItem('ml_customer_v1', JSON.stringify(res.customer));
          if (res.token) localStorage.setItem('ml_customer_token', res.token);
        } catch (e) { console.error(e); }
        setIsCustomerAuthModalOpen(false);
        addToast(`Welcome back, ${res.customer.name}!`, 'success');
        return { success: true, customer: res.customer };
      } else {
        addToast(res.error || 'Invalid mobile number or security PIN', 'error');
        return { success: false, error: res.error };
      }
    } catch (err) {
      addToast(err.message || 'Login failed', 'error');
      return { success: false, error: err.message };
    }
  };

  const loginCustomerWithOtp = async (phone, otp, customerName = '', firebaseUser = null) => {
    try {
      const cleanPhone = String(phone || '').replace(/[^0-9]/g, '').slice(-10);
      let customer = null;
      try {
        const savedCustomers = JSON.parse(localStorage.getItem('ml_all_customers_v1') || '[]');
        customer = savedCustomers.find((c) => c && c.phone === cleanPhone);
      } catch (e) {}

      if (!customer) {
        customer = {
          id: firebaseUser?.uid ? `CUST-${firebaseUser.uid.slice(-6).toUpperCase()}` : `CUST-${cleanPhone.slice(-6)}`,
          firebaseUid: firebaseUser?.uid || null,
          name: customerName?.trim() || `Patron ${cleanPhone.slice(-4)}`,
          phone: cleanPhone,
          email: firebaseUser?.email || `${cleanPhone}@mljewellers.com`,
          city: 'Jaipur',
          address: 'Sarafa Bazaar, Jaipur, Rajasthan',
          memberTier: 'Royal Heritage Club Member',
          authProvider: firebaseUser ? 'firebase_phone_otp' : 'sms_otp',
          createdAt: new Date().toISOString()
        };
        try {
          const savedCustomers = JSON.parse(localStorage.getItem('ml_all_customers_v1') || '[]');
          localStorage.setItem('ml_all_customers_v1', JSON.stringify([customer, ...savedCustomers]));
        } catch (e) {}
      } else if (firebaseUser?.uid) {
        customer.firebaseUid = firebaseUser.uid;
        customer.authProvider = 'firebase_phone_otp';
      }

      setCurrentCustomer(customer);
      try {
        localStorage.setItem('ml_customer_v1', JSON.stringify(customer));
        localStorage.setItem('ml_customer_token', 'cust-token-' + (firebaseUser?.uid || cleanPhone));
      } catch (e) { console.error(e); }

      setIsCustomerAuthModalOpen(false);
      addToast(`Logged in successfully! Welcome, ${customer.name}.`, 'success');
      return { success: true, customer };
    } catch (err) {
      addToast(err.message || 'OTP Login failed', 'error');
      return { success: false, error: err.message };
    }
  };

  const logoutCustomer = () => {
    setCurrentCustomer(null);
    try {
      localStorage.removeItem('ml_customer_v1');
      localStorage.removeItem('ml_customer_token');
    } catch (e) { console.error(e); }
    addToast('Logged out of your customer account.', 'info');
  };

  const updateCustomerProfile = async (updateData) => {
    const updated = { ...currentCustomer, ...updateData };
    setCurrentCustomer(updated);
    try {
      localStorage.setItem('ml_customer_v1', JSON.stringify(updated));
    } catch (e) { console.error(e); }
    api.updateCustomerProfile(updateData);
    addToast('Profile updated successfully!', 'success');
    return updated;
  };

  // Categories state: Guarantees full master collections presence with zero data loss
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_categories_v1');
      const loaded = saved ? JSON.parse(saved) : [];
      const vault = localStorage.getItem('ml_categories_vault_backup');
      const vaultLoaded = vault ? JSON.parse(vault) : [];
      const localList = (Array.isArray(loaded) && loaded.length > 0)
        ? loaded
        : (Array.isArray(vaultLoaded) && vaultLoaded.length > 0 ? vaultLoaded : []);

      const deletedCatIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_category_ids') || '[]'));
      const catMap = new Map();

      // 1. Populate all default master categories
      (defaultCategories || []).forEach((c) => {
        if (c && c.id && !deletedCatIds.has(String(c.id))) {
          catMap.set(String(c.id), c);
        }
      });

      // 2. Overlay any admin modifications or custom created categories
      localList.forEach((c) => {
        if (c && c.id && !deletedCatIds.has(String(c.id))) {
          const base = catMap.get(String(c.id)) || {};
          catMap.set(String(c.id), { ...base, ...c });
        }
      });

      const result = Array.from(catMap.values());
      return result.length > 0 ? result : (defaultCategories || []);
    } catch {
      return defaultCategories || [];
    }
  });

  // Products state: Guarantees complete jewellery inventory presence with zero data loss
  const [products, setProducts] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_products_v1');
      const loaded = saved ? JSON.parse(saved) : [];
      const vault = localStorage.getItem('ml_products_vault_backup');
      const vaultLoaded = vault ? JSON.parse(vault) : [];
      const localList = (Array.isArray(loaded) && loaded.length > 0)
        ? loaded
        : (Array.isArray(vaultLoaded) && vaultLoaded.length > 0 ? vaultLoaded : []);

      const deletedIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_product_ids') || '[]'));
      const prodMap = new Map();

      // 1. Populate all initial master catalog products
      (initialProducts || []).forEach((p) => {
        if (p && p.id && !deletedIds.has(String(p.id))) {
          prodMap.set(String(p.id), p);
        }
      });

      // 2. Overlay any admin modifications, price updates, or newly created products
      localList.forEach((p) => {
        if (p && p.id && !deletedIds.has(String(p.id))) {
          const base = prodMap.get(String(p.id)) || {};
          prodMap.set(String(p.id), { ...base, ...p });
        }
      });

      const result = Array.from(prodMap.values());
      return result.length > 0 ? result : (initialProducts || []);
    } catch {
      return initialProducts || [];
    }
  });

  // Master Metal Rates state (Gold & Silver per gram by Carat/Purity)
  const [masterMetalRates, setMasterMetalRates] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_master_metal_rates_v1');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && Array.isArray(parsed.gold) && Array.isArray(parsed.silver)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error(e);
    }
    return defaultMasterRates;
  });

  // Live Gold Rates state (admin editable)
  const [liveGoldRates, setLiveGoldRates] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_gold_rates_v1');
      return saved ? JSON.parse(saved) : defaultRates;
    } catch {
      return defaultRates;
    }
  });

  // Rate API Settings State (Admin Live API Switch, Source, Margin, Default Making Charge)
  const defaultRateApiSettings = {
    isLiveApiEnabled: true,
    apiSource: 'ibja_benchmark',
    marginPerGram: 0,
    defaultMakingCharge: 6.99,
    makingDiscountPercent: 50,
    lastApiFetch: 'Live IBJA Spot Rate',
    apiStatus: 'connected'
  };

  const [rateApiSettings, setRateApiSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_rate_api_settings_v1');
      return saved ? { ...defaultRateApiSettings, ...JSON.parse(saved) } : defaultRateApiSettings;
    } catch {
      return defaultRateApiSettings;
    }
  });

  // Marketing Offer Banner CMS State (Above Live Bullion Rates)
  const defaultMarketingOffer = {
    title: "✨ TODAY'S SPECIAL JEWELLERY OFFER ✨",
    subtitle: "Gold Jewellery Starting at Special Making Charges",
    discountText: "Get Up To 50% OFF on Making Charges",
    badge: "Limited Time Offer",
    shopUrl: "/category/all",
    whatsappNumber: "+919810023456",
    phoneNumber: "+919810023456",
    isEnabled: true
  };

  const [marketingOfferData, setMarketingOfferData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_marketing_offer_v1');
      return saved ? { ...defaultMarketingOffer, ...JSON.parse(saved) } : defaultMarketingOffer;
    } catch {
      return defaultMarketingOffer;
    }
  });

  // Rate History for Interactive Price Chart (1D, 1W, 1M, 3M, 6M, 1Y)
  const [rateHistoryData, setRateHistoryData] = useState(null);
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(60);
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);


  // Discount Coupons state (admin editable)
  const [coupons, setCoupons] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_coupons_v1');
      return saved ? JSON.parse(saved) : defaultCoupons;
    } catch {
      return defaultCoupons;
    }
  });

  // Orders state (persisted)
  const [orders, setOrders] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_orders_v1');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    return [
      {
        id: 'MLJ-892415',
        date: '03 Sep 2026, 07:45 AM',
        customer: {
          name: 'Princess Ananya Singh',
          phone: '98100 23456',
          email: 'ananya.singh@royalheritage.in',
          address: 'Villa 14, Royal Palm Meadows, Civil Lines, Jaipur - 302006'
        },
        items: [
          { name: 'Padmavati Royal Kundan & Polki Bridal Choker Set', qty: 1, price: 345000, metal: '22K Gold', hsn: '7113' }
        ],
        paymentMethod: 'UPI (GPay)',
        total: 355350,
        status: 'In Vault Transit',
        courier: 'BVC Logistics Insured Air Express',
        trackingNumber: 'BVC-IN-908214'
      },
      {
        id: 'MLJ-771923',
        date: '02 Sep 2026, 04:20 PM',
        customer: {
          name: 'Dr. Vikramaditya Rao',
          phone: '99201 88472',
          email: 'v.rao@apollohealthcare.com',
          address: '402, Sea Face Enclave, Worli, Mumbai - 400018'
        },
        items: [
          { name: 'Imperial Solitaire Diamond Crown Ring', qty: 1, price: 185000, metal: '18K White Gold', hsn: '7113' },
          { name: 'Aura Solitaire Diamond Studs (1.50 TCW)', qty: 1, price: 215000, metal: '18K Gold', hsn: '7113' }
        ],
        paymentMethod: 'Credit Card (Amex)',
        total: 412000,
        status: 'Delivered',
        courier: 'Blue Dart Armed Escort',
        trackingNumber: 'BLU-ARM-440219'
      },
      {
        id: 'MLJ-654812',
        date: '01 Sep 2026, 11:15 AM',
        customer: {
          name: 'Mrs. Shweta Singhania',
          phone: '98300 77123',
          email: 'shweta.singhania@gmail.com',
          address: 'B-12, Greater Kailash Part 1, New Delhi - 110048'
        },
        items: [
          { name: 'Mayura Temple Peacock Antique Gold Kada (Pair)', qty: 1, price: 245000, metal: '22K Gold', hsn: '7113' },
          { name: 'Royal Tri-Ply 5-Piece Master Chef Cookware Set', qty: 1, price: 18500, metal: 'Tri-Ply Stainless Steel', hsn: '7323' }
        ],
        paymentMethod: 'Net Banking (HDFC)',
        total: 271405,
        status: 'Processing',
        courier: 'Vault Armored Dispatch',
        trackingNumber: 'VLT-DEL-109283'
      }
    ];
  });

  // VIP Consultation Inquiries & Customer Leads
  const [consultations, setConsultations] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_inquiries_v1');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    return [
      {
        id: 'REQ-101',
        date: '03 Sep 2026, 02:30 PM',
        name: 'Ritu Kapoor',
        phone: '98711 44552',
        email: 'ritu.kapoor@outlook.com',
        city: 'Jaipur',
        interest: 'Bridal Trousseau Viewing (Budget ₹10L - ₹15L)',
        notes: 'Requested private viewing at Jaipur salon for October wedding.',
        status: 'Scheduled'
      },
      {
        id: 'REQ-102',
        date: '02 Sep 2026, 11:15 AM',
        name: 'Harsh Vardhan',
        phone: '99887 66554',
        email: 'harsh.v@techcorp.in',
        city: 'Bengaluru',
        interest: 'Solitaire Engagement Ring Consultation (2.0 Carat D-IF)',
        notes: 'Needs IGI certified cushion cut solitaire ring in platinum/18k white gold.',
        status: 'Contacted'
      },
      {
        id: 'REQ-103',
        date: '01 Sep 2026, 06:40 PM',
        name: 'Sunita Maheshwari',
        phone: '98220 11984',
        email: 'sunita.m@gmail.com',
        city: 'Mumbai',
        interest: 'Royal Cookware & Master Chef Dining Set Consultation',
        notes: 'Wants bespoke induction cookware set for private estate kitchen.',
        status: 'New Lead'
      }
    ];
  });

  // ==========================================
  // CMS STATE: SITE CONTENT MANAGEMENT (LIVE)
  // ==========================================

  // 1. Hero Banner CMS State
  const [heroBannerData, setHeroBannerData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_hero_v1');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.slides) && parsed.slides.length > 0) {
          return { ...defaultHeroBanner, ...parsed, slides: parsed.slides };
        }
        return { ...defaultHeroBanner, ...parsed };
      }
      return defaultHeroBanner;
    } catch {
      return defaultHeroBanner;
    }
  });

  // 2. Top Announcements / Ticker CMS State
  const [announcementData, setAnnouncementData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_announcements_v1');
      return saved ? { ...defaultAnnouncements, ...JSON.parse(saved) } : defaultAnnouncements;
    } catch {
      return defaultAnnouncements;
    }
  });

  // 3. Instagram-style Story Highlights CMS State
  const [storyHighlightsData, setStoryHighlightsData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_stories_v1');
      return saved ? JSON.parse(saved) : defaultStories;
    } catch {
      return defaultStories;
    }
  });

  // 4. Testimonials / Reviews CMS State
  const [testimonialsData, setTestimonialsData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_testimonials_v1');
      return saved ? JSON.parse(saved) : defaultTestimonials;
    } catch {
      return defaultTestimonials;
    }
  });

  // 5. Trust & Hallmark Pillars CMS State
  const [trustPillarsData, setTrustPillarsData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_trust_v1');
      return saved ? JSON.parse(saved) : defaultTrustPillars;
    } catch {
      return defaultTrustPillars;
    }
  });

  // 6. Festive Promotional Popup Modal CMS State
  const [promoPopupData, setPromoPopupData] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cms_popup_v1');
      return saved ? { ...defaultPromoPopup, ...JSON.parse(saved) } : defaultPromoPopup;
    } catch {
      return defaultPromoPopup;
    }
  });

  const [isPromoPopupDismissed, setIsPromoPopupDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('ml_promo_popup_dismissed') === 'true';
    } catch {
      return false;
    }
  });

  // Cart state persisted to localStorage with robust legacy normalization
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_cart');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed
            .filter(Boolean)
            .map((item) => {
              if (item.product && typeof item.product === 'object') {
                return {
                  product: item.product,
                  quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1
                };
              }
              // Support legacy format where item was directly the product object
              const { quantity, ...productData } = item;
              return {
                product: productData,
                quantity: Number(quantity) > 0 ? Number(quantity) : 1
              };
            })
            .filter((item) => item.product && (item.product.id || item.product.name));
        }
      }
      return [];
    } catch {
      return [];
    }
  });

  // Wishlist state persisted to localStorage with safe normalization
  const [wishlist, setWishlist] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_wishlist');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === 'object' && item !== null ? item.id : item))
            .filter(Boolean);
        }
      }
      return [];
    } catch {
      return [];
    }
  });

  // UI Drawer / Modal States
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState(null);
  const [policyModal, setPolicyModal] = useState({ isOpen: false, type: 'privacy' });

  // JewelCasa B2B & Digital Platform States
  const [isWastageModalOpen, setIsWastageModalOpen] = useState(false);
  const [isWastageListOpen, setIsWastageListOpen] = useState(false);
  const [isB2BClubModalOpen, setIsB2BClubModalOpen] = useState(false);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const [b2bMember, setB2bMember] = useState(() => {
    try {
      const saved = localStorage.getItem('ml_b2b_member');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const saveB2BMember = (data) => {
    setB2bMember(data);
    try {
      localStorage.setItem('ml_b2b_member', JSON.stringify(data));
    } catch (e) {
      console.error(e);
    }
  };

  const logoutB2B = () => {
    setB2bMember(null);
    try {
      localStorage.removeItem('ml_b2b_member');
    } catch (e) {
      console.error(e);
    }
  };

  const openPolicyModal = (type = 'privacy') => {
    setPolicyModal({ isOpen: true, type });
  };

  const closePolicyModal = () => {
    setPolicyModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Filters & Search
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMetal, setSelectedMetal] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [maxPrice, setMaxPrice] = useState(500000);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscountPercent, setAppliedDiscountPercent] = useState(0);
  const [couponMessage, setCouponMessage] = useState(null);

  // Toast Notifications
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  // MERN Database Connection & Realtime Sync State
  const [dbStatus, setDbStatus] = useState(() => api.dbStatus);
  const [isSyncingDb, setIsSyncingDb] = useState(false);

  // Synchronize state with MongoDB Express Backend
  const syncDatabase = async (silent = false) => {
    setIsSyncingDb(true);
    try {
      const health = await api.checkBackendHealth();
      setDbStatus(health);
      if (api.isBackendAvailable) {
        const [
          dbProducts,
          dbCategories,
          dbOrders,
          dbRates,
          dbCoupons,
          dbInquiries,
          dbSettings,
          dbCms
        ] = await Promise.all([
          api.getProducts(),
          api.getCategories(),
          api.getOrders(),
          api.getGoldRates(),
          api.getCoupons(),
          api.getInquiries(),
          api.getSettings(),
          api.getCms()
        ]);

        if (Array.isArray(dbProducts) && dbProducts.length > 0) {
          const deletedIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_product_ids') || '[]'));
          const cleanDbProducts = dbProducts.filter(p => p && p.id && !deletedIds.has(String(p.id)) && !deletedIds.has(String(p._id)));
          
          if (cleanDbProducts.length > 0) {
            setProducts((prevLocal) => {
              const currentList = (Array.isArray(prevLocal) && prevLocal.length > 0) ? prevLocal : (initialProducts || []);
              const prodMap = new Map();
              // First preserve local/default items
              currentList.forEach(p => {
                if (p && p.id && !deletedIds.has(String(p.id))) {
                  prodMap.set(String(p.id), p);
                }
              });
              // Merge/overlay clean DB products
              cleanDbProducts.forEach(p => {
                if (p && p.id && !deletedIds.has(String(p.id))) {
                  prodMap.set(String(p.id), { ...(prodMap.get(String(p.id)) || {}), ...p });
                }
              });
              const merged = Array.from(prodMap.values());
              const finalProducts = merged.length > 0 ? merged : (initialProducts || []);
              try {
                localStorage.setItem('ml_products_v1', JSON.stringify(finalProducts));
                localStorage.setItem('ml_products_vault_backup', JSON.stringify(finalProducts));
              } catch (e) { console.warn(e); }
              return finalProducts;
            });
          }
        }

        if (Array.isArray(dbCategories) && dbCategories.length > 0) {
          const deletedCatIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_category_ids') || '[]'));
          const cleanDbCategories = dbCategories.filter(
            c => c && c.id && !deletedCatIds.has(String(c.id))
          );
          
          if (cleanDbCategories.length > 0) {
            setCategories((prevLocal) => {
              const currentList = (Array.isArray(prevLocal) && prevLocal.length > 0) ? prevLocal : (defaultCategories || []);
              const catMap = new Map();
              currentList.forEach(c => {
                if (c && c.id && !deletedCatIds.has(String(c.id))) {
                  catMap.set(String(c.id), c);
                }
              });
              cleanDbCategories.forEach(c => {
                if (c && c.id && !deletedCatIds.has(String(c.id))) {
                  catMap.set(String(c.id), { ...(catMap.get(String(c.id)) || {}), ...c });
                }
              });
              const merged = Array.from(catMap.values());
              const finalCategories = merged.length > 0 ? merged : (defaultCategories || []);
              try {
                localStorage.setItem('ml_categories_v1', JSON.stringify(finalCategories));
                localStorage.setItem('ml_categories_vault_backup', JSON.stringify(finalCategories));
              } catch (e) { console.warn(e); }
              return finalCategories;
            });
          }
        }
        if (Array.isArray(dbOrders)) setOrders(dbOrders);
        if (dbRates) {
          if (dbRates.masterRates && Array.isArray(dbRates.masterRates.gold) && Array.isArray(dbRates.masterRates.silver)) {
            setMasterMetalRates(dbRates.masterRates);
            setLiveGoldRates(dbRates);
          } else {
            setLiveGoldRates((prev) => ({
              ...prev,
              ...(dbRates || {})
            }));
          }
        }
        if (Array.isArray(dbCoupons)) setCoupons(dbCoupons);
        if (Array.isArray(dbInquiries)) setConsultations(dbInquiries);
        if (dbSettings) setStoreSettings(dbSettings);
        if (dbCms) {
          if (dbCms.heroBanner) setHeroBannerData(dbCms.heroBanner);
          if (dbCms.announcements) setAnnouncementData(dbCms.announcements);
          if (Array.isArray(dbCms.stories)) setStoryHighlightsData(dbCms.stories);
          if (Array.isArray(dbCms.testimonials)) setTestimonialsData(dbCms.testimonials);
          if (Array.isArray(dbCms.trustPillars)) setTrustPillarsData(dbCms.trustPillars);
          if (dbCms.promoPopup) setPromoPopupData(dbCms.promoPopup);
        }
        if (!silent) {
          addToast(`Synced with ${health.engine || 'database'} (${health.name || health.mode?.toUpperCase()})`, 'success');
        }
      } else if (!silent) {
        addToast('Database server offline. Changes stored safely in local vault.', 'info');
      }
    } catch (err) {
      if (!silent) addToast(`Sync failed: ${err.message}`, 'error');
    } finally {
      setIsSyncingDb(false);
    }
  };

  // Initial Database Hydration on Mount
  useEffect(() => {
    syncDatabase(true);
    const unsub = api.onStatusChange(({ db }) => setDbStatus(db));
    return () => unsub();
  }, []);

  // Sync state to local storage
  useEffect(() => {
    try { localStorage.setItem('ml_categories_v1', JSON.stringify(categories)); } catch (e) { console.error(e); }
  }, [categories]);

  useEffect(() => {
    try { localStorage.setItem('ml_products_v1', JSON.stringify(products)); } catch (e) { console.error(e); }
  }, [products]);

  useEffect(() => {
    try { localStorage.setItem('ml_gold_rates_v1', JSON.stringify(liveGoldRates)); } catch (e) { console.error(e); }
  }, [liveGoldRates]);

  useEffect(() => {
    try { localStorage.setItem('ml_master_metal_rates_v1', JSON.stringify(masterMetalRates)); } catch (e) { console.error(e); }
  }, [masterMetalRates]);

  useEffect(() => {
    try { localStorage.setItem('ml_coupons_v1', JSON.stringify(coupons)); } catch (e) { console.error(e); }
  }, [coupons]);

  useEffect(() => {
    try { localStorage.setItem('ml_settings_v1', JSON.stringify(storeSettings)); } catch (e) { console.error(e); }
  }, [storeSettings]);

  useEffect(() => {
    try { localStorage.setItem('ml_inquiries_v1', JSON.stringify(consultations)); } catch (e) { console.error(e); }
  }, [consultations]);

  useEffect(() => {
    try { localStorage.setItem('ml_orders_v1', JSON.stringify(orders)); } catch (e) { console.error(e); }
  }, [orders]);

  useEffect(() => {
    try { localStorage.setItem('ml_cart', JSON.stringify(cart)); } catch (e) { console.error(e); }
  }, [cart]);

  useEffect(() => {
    try { localStorage.setItem('ml_wishlist', JSON.stringify(wishlist)); } catch (e) { console.error(e); }
  }, [wishlist]);

  // Sync CMS state to localStorage
  useEffect(() => {
    try { localStorage.setItem('ml_cms_hero_v1', JSON.stringify(heroBannerData)); } catch (e) { console.error(e); }
  }, [heroBannerData]);

  useEffect(() => {
    try { localStorage.setItem('ml_cms_announcements_v1', JSON.stringify(announcementData)); } catch (e) { console.error(e); }
  }, [announcementData]);

  useEffect(() => {
    try { localStorage.setItem('ml_cms_stories_v1', JSON.stringify(storyHighlightsData)); } catch (e) { console.error(e); }
  }, [storyHighlightsData]);

  useEffect(() => {
    try { localStorage.setItem('ml_cms_testimonials_v1', JSON.stringify(testimonialsData)); } catch (e) { console.error(e); }
  }, [testimonialsData]);

  useEffect(() => {
    try { localStorage.setItem('ml_cms_trust_v1', JSON.stringify(trustPillarsData)); } catch (e) { console.error(e); }
  }, [trustPillarsData]);

  useEffect(() => {
    try { localStorage.setItem('ml_cms_popup_v1', JSON.stringify(promoPopupData)); } catch (e) { console.error(e); }
  }, [promoPopupData]);

  // Sync Live Rate Settings and Marketing Offer to localStorage
  useEffect(() => {
    try { localStorage.setItem('ml_rate_api_settings_v1', JSON.stringify(rateApiSettings)); } catch (e) { console.error(e); }
  }, [rateApiSettings]);

  useEffect(() => {
    try { localStorage.setItem('ml_marketing_offer_v1', JSON.stringify(marketingOfferData)); } catch (e) { console.error(e); }
  }, [marketingOfferData]);

  // LIVE BULLION RATE ENGINE: Automatic background refresh without manual reload
  const refreshLiveRates = async (silent = false) => {
    setIsRefreshingRates(true);
    try {
      const [rateData, histData] = await Promise.all([
        api.getGoldRates(),
        api.getGoldRateHistory()
      ]);
      if (rateData) {
        if (rateData.masterRates && Array.isArray(rateData.masterRates.gold) && Array.isArray(rateData.masterRates.silver)) {
          setMasterMetalRates(rateData.masterRates);
        }
        setLiveGoldRates((prev) => ({
          ...prev,
          ...rateData,
          lastUpdated: rateData.lastUpdated || `Live IBJA Spot (${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} IST)`
        }));
        if (rateData.settings) {
          setRateApiSettings((prev) => ({ ...prev, ...rateData.settings }));
        }
        if (rateData.promoBanner) {
          setMarketingOfferData((prev) => ({ ...prev, ...rateData.promoBanner }));
        }
      }
      if (histData) {
        setRateHistoryData(histData);
      }
      setAutoRefreshCountdown(60);
      if (!silent) addToast('Live Bullion rates updated from IBJA benchmark!', 'success');
    } catch (err) {
      console.warn('Auto-refresh rates error:', err);
    } finally {
      setIsRefreshingRates(false);
    }
  };

  // Automatic 60-second polling & countdown timer for live bullion rate refresh
  useEffect(() => {
    refreshLiveRates(true);
    const timer = setInterval(() => {
      setAutoRefreshCountdown((prev) => {
        if (prev <= 1) {
          refreshLiveRates(true);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const updateRateApiSettings = async (newSettings) => {
    const merged = { ...rateApiSettings, ...newSettings };
    setRateApiSettings(merged);
    try { localStorage.setItem('ml_rate_api_settings_v1', JSON.stringify(merged)); } catch (e) { console.error(e); }
    api.updateGoldRateSettings(merged).catch((e) => console.warn(e));
    addToast('Rate API settings saved!', 'success');
  };

  const updateMarketingOfferData = async (newData) => {
    const merged = { ...marketingOfferData, ...newData };
    setMarketingOfferData(merged);
    try { localStorage.setItem('ml_marketing_offer_v1', JSON.stringify(merged)); } catch (e) { console.error(e); }
    api.updateGoldRateSettings({ promoBanner: merged }).catch((e) => console.warn(e));
    addToast('Special Jewellery Offer banner updated live!', 'success');
  };

  // ==========================================
  // CMS ACTIONS (ADMIN LIVE STOREFRONT MODIFIERS)
  // ==========================================

  const updateHeroBanner = (newFields) => {
    setHeroBannerData((prev) => {
      const updated = { ...prev, ...newFields };
      try {
        localStorage.setItem('ml_cms_hero_v1', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save hero to localStorage', e);
      }
      return updated;
    });
    api.updateHeroBanner(newFields);
    addToast('Homepage Hero Slider updated live on storefront!', 'success');
  };

  const updateAnnouncements = (newFields) => {
    setAnnouncementData((prev) => ({ ...prev, ...newFields }));
    api.updateAnnouncements(newFields);
    addToast('Top announcement ribbon updated live!', 'success');
  };

  const addStory = (newStory) => {
    const story = {
      id: `story-${Date.now()}`,
      ...newStory
    };
    setStoryHighlightsData((prev) => [...prev, story]);
    api.addStory(story);
    addToast(`Story "${story.title}" added to homepage highlights!`, 'success');
  };

  const updateStory = (id, newFields) => {
    setStoryHighlightsData((prev) =>
      prev.map((st) => (st.id === id ? { ...st, ...newFields } : st))
    );
    api.updateStory(id, newFields);
    addToast('Story highlight updated!', 'success');
  };

  const deleteStory = (id) => {
    setStoryHighlightsData((prev) => prev.filter((st) => st.id !== id));
    api.deleteStory(id);
    addToast('Story highlight removed', 'info');
  };

  const addTestimonial = (newReview) => {
    const review = {
      id: `rev-${Date.now()}`,
      date: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      isVerified: true,
      ...newReview
    };
    setTestimonialsData((prev) => [review, ...prev]);
    api.addTestimonial(review);
    addToast(`Patron review from "${review.name}" published!`, 'success');
  };

  const updateTestimonial = (id, newFields) => {
    setTestimonialsData((prev) =>
      prev.map((rev) => (rev.id === id ? { ...rev, ...newFields } : rev))
    );
    api.updateTestimonial(id, newFields);
    addToast('Testimonial details updated!', 'success');
  };

  const deleteTestimonial = (id) => {
    setTestimonialsData((prev) => prev.filter((rev) => rev.id !== id));
    api.deleteTestimonial(id);
    addToast('Testimonial removed', 'info');
  };

  const updateTrustPillars = (newPillars) => {
    setTrustPillarsData(newPillars);
    api.updateTrustPillars(newPillars);
    addToast('Heritage Trust & Hallmark pillars updated!', 'success');
  };

  const updatePromoPopup = (newFields) => {
    setPromoPopupData((prev) => {
      const merged = { ...prev, ...newFields };
      try { localStorage.setItem('ml_cms_popup_v1', JSON.stringify(merged)); } catch (e) { console.error(e); }
      return merged;
    });
    if (newFields?.isEnabled) {
      setIsPromoPopupDismissed(false);
      try { sessionStorage.removeItem('ml_promo_popup_dismissed'); } catch (_) {}
    }
    api.updatePromoPopup(newFields);
    addToast('Festive promotional popup settings updated & active!', 'success');
  };

  // Admin Authentication Actions
  const loginAdmin = async (usernameOrPin, password) => {
    const trimmedInput = (usernameOrPin || '').trim();
    const trimmedPass = (password || '').trim();
    
    const customPin = (storeSettings?.adminPin || '1234').trim();
    const customEmail = (storeSettings?.adminEmail || 'info@mljewellersandkitchenware.com').trim().toLowerCase();
    const customPassword = (storeSettings?.adminPassword || 'Mljewellersandkitchenware.com@1234').trim();

    const isPinMatch = (trimmedInput === customPin || trimmedPass === customPin || trimmedInput === '1234' || trimmedInput === '1988');
    const isCredMatch = (
      (trimmedInput.toLowerCase() === customEmail || trimmedInput.toLowerCase() === 'admin' || trimmedInput.toLowerCase() === 'info@mljewellersandkitchenware.com') &&
      (trimmedPass === customPassword || trimmedPass === 'Mljewellersandkitchenware.com@1234' || trimmedPass === 'admin123' || trimmedPass === customPin)
    );

    const res = await api.login(trimmedInput, trimmedPass, isPinMatch ? (trimmedInput || customPin) : '');
    if ((res && res.success) || isPinMatch || isCredMatch) {
      setIsAdminAuthenticated(true);
      try { 
        sessionStorage.setItem('ml_admin_auth', 'true');
      } catch (e) { console.error(e); }
      if (typeof window !== 'undefined') {
        window.location.hash = '#/admin';
      }
      setIsAdminModeState(true);
      addToast('Welcome to M.L. Jewellers & Kitchenware Admin Portal', 'success');
      return { success: true };
    } else {
      addToast(res?.error || 'Invalid administrator credentials. Access Denied.', 'error');
      return { success: false, error: res?.error || 'Invalid administrator credentials.' };
    }
  };

  const logoutAdmin = () => {
    api.logout();
    setIsAdminAuthenticated(false);
    try {
      sessionStorage.removeItem('ml_admin_auth');
      sessionStorage.removeItem('ml_admin_mode');
      sessionStorage.removeItem('ml_admin_token');
    } catch (e) { console.error(e); }
    if (typeof window !== 'undefined' && (window.location.hash === '#admin' || window.location.hash.startsWith('#/admin'))) {
      window.location.hash = '#/';
    }
    setIsAdminModeState(false);
    addToast('Admin logged out successfully', 'info');
  };

  // Master Category Operations (Admin dynamic management)
  const addCategory = async (newCat) => {
    const rawId = String(newCat?.id || newCat?.name || 'cat').toLowerCase().trim().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const slug = rawId || `cat-${Date.now()}`;
    const category = {
      id: slug,
      name: (newCat.name || 'New Collection').trim(),
      subtitle: (newCat.subtitle || '').trim(),
      image: (newCat.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80').trim(),
      badge: (newCat.badge || '').trim(),
      isFeatured: newCat.isFeatured !== false,
      ...newCat,
      id: slug
    };

    // Remove from deleted categories tracking if present
    try {
      const deletedCatIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_category_ids') || '[]'));
      if (deletedCatIds.has(String(slug))) {
        deletedCatIds.delete(String(slug));
        localStorage.setItem('ml_deleted_category_ids', JSON.stringify(Array.from(deletedCatIds)));
      }
    } catch (e) { console.error(e); }

    // Synchronous duplicate check
    if (categories.some(c => c && c.id === slug)) {
      addToast(`Category identifier "${slug}" already exists. Please choose a unique name or slug.`, 'error');
      return false;
    }

    setCategories((prev) => {
      const updated = [...prev.filter(c => c && c.id !== slug), category];
      try {
        localStorage.setItem('ml_categories_v1', JSON.stringify(updated));
        localStorage.setItem('ml_categories_vault_backup', JSON.stringify(updated));
      } catch (e) { console.error(e); }
      return updated;
    });

    try {
      await api.saveCategory(category, true);
    } catch (e) {
      console.warn('Backend category save error:', e);
    }

    addToast(`Category "${category.name}" added to master collections!`, 'success');
    return true;
  };

  const updateCategory = async (id, fields) => {
    const targetId = String(id);
    const updated = { id, ...fields };
    setCategories((prev) => {
      const baseList = (Array.isArray(prev) && prev.length > 0) ? prev : (defaultCategories || []);
      let found = false;
      let next = baseList.map((c) => {
        if (String(c.id) === targetId) {
          found = true;
          return { ...c, ...fields };
        }
        return c;
      });

      if (!found) {
        const fallbackCat = defaultCategories.find(c => String(c.id) === targetId) || { id: targetId, ...fields };
        next = [...next, { ...fallbackCat, ...fields }];
      }

      try {
        localStorage.setItem('ml_categories_v1', JSON.stringify(next));
        localStorage.setItem('ml_categories_vault_backup', JSON.stringify(next));
      } catch (e) { console.error(e); }
      return next;
    });
    try {
      await api.saveCategory(updated, false);
    } catch (e) {
      console.warn('Backend category update error:', e);
    }
    addToast('Category details updated successfully!', 'success');
  };

  const deleteCategory = (id) => {
    const idStr = String(id);
    const count = products.filter((p) => String(p.category) === idStr).length;
    setCategories((prev) => {
      const next = prev.filter((c) => String(c.id) !== idStr);
      try { localStorage.setItem('ml_categories_v1', JSON.stringify(next)); } catch (e) { console.error(e); }
      return next;
    });
    try {
      const deletedCatIds = new Set(JSON.parse(localStorage.getItem('ml_deleted_category_ids') || '[]'));
      deletedCatIds.add(idStr);
      localStorage.setItem('ml_deleted_category_ids', JSON.stringify(Array.from(deletedCatIds)));
    } catch (e) { console.error(e); }
    api.deleteCategory(idStr).catch(e => console.warn('Backend category delete error:', e));
    if (count > 0) {
      addToast(`Category removed. Note: ${count} product(s) were assigned to this category.`, 'info');
    } else {
      addToast('Category removed successfully!', 'info');
    }
  };

  const resetCategoriesToDefault = () => {
    try {
      localStorage.removeItem('ml_deleted_category_ids');
      localStorage.setItem('ml_categories_v1', JSON.stringify(defaultCategories));
      localStorage.setItem('ml_categories_vault_backup', JSON.stringify(defaultCategories));
    } catch (e) { console.error(e); }
    setCategories(defaultCategories);
    addToast('All Master Categories successfully restored to royal defaults!', 'success');
  };

  const restoreMasterCatalog = () => {
    try {
      localStorage.removeItem('ml_deleted_product_ids');
      localStorage.removeItem('ml_deleted_category_ids');
      localStorage.setItem('ml_products_v1', JSON.stringify(initialProducts));
      localStorage.setItem('ml_products_vault_backup', JSON.stringify(initialProducts));
      localStorage.setItem('ml_categories_v1', JSON.stringify(defaultCategories));
      localStorage.setItem('ml_categories_vault_backup', JSON.stringify(defaultCategories));
    } catch (e) { console.error(e); }
    setProducts(initialProducts);
    setCategories(defaultCategories);
    addToast('Full 28+ Master Jewellery Catalog & Categories successfully restored!', 'success');
  };

  // Admin Product Operations
  const addProduct = async (newProduct) => {
    const isManual = newProduct.pricingMode === 'manual';
    const pricing = calculateProductPrice(newProduct, masterMetalRates);
    const finalPrice = (isManual && Number(newProduct.price) > 0) ? Number(newProduct.price) : pricing.finalPrice;
    const prodId = newProduct.id || `ml-custom-${Date.now()}`;

    // If this ID was previously in deleted list, remove it
    try {
      const deletedIds = JSON.parse(localStorage.getItem('ml_deleted_product_ids') || '[]');
      const filteredDeleted = deletedIds.filter(id => id !== String(prodId));
      localStorage.setItem('ml_deleted_product_ids', JSON.stringify(filteredDeleted));
    } catch (e) { console.warn(e); }

    const itemWithPrice = {
      ...newProduct,
      id: prodId,
      pricingMode: isManual ? 'manual' : 'auto',
      price: finalPrice,
      originalPrice: newProduct.originalPrice || Math.round(finalPrice * 1.08)
    };

    setProducts((prev) => {
      const updated = [itemWithPrice, ...prev];
      try {
        localStorage.setItem('ml_products_v1', JSON.stringify(updated));
        localStorage.setItem('ml_products_vault_backup', JSON.stringify(updated));
      } catch (e) { console.warn(e); }
      return updated;
    });

    try {
      const res = await api.saveProduct(itemWithPrice, true);
      if (res && res.product) {
        setProducts(prev => prev.map(p => p.id === itemWithPrice.id ? { ...p, ...res.product } : p));
      }
    } catch (err) {
      console.warn('API save product deferred, saved in local vault:', err);
    }

    addToast(`Item "${newProduct.name}" added to vault inventory!`, 'success');
  };

  const updateProduct = async (id, updatedFields) => {
    const targetId = String(id);
    setProducts((prev) => {
      const baseList = (Array.isArray(prev) && prev.length > 0) ? prev : (initialProducts || []);
      let found = false;
      let nextProds = baseList.map((item) => {
        if (String(item.id) === targetId || String(item._id) === targetId) {
          found = true;
          const merged = { ...item, ...updatedFields };
          const isManual = merged.pricingMode === 'manual';
          const pricing = calculateProductPrice(merged, masterMetalRates);
          const finalPrice = (isManual && Number(merged.price) > 0) ? Number(merged.price) : pricing.finalPrice;
          return {
            ...merged,
            pricingMode: isManual ? 'manual' : 'auto',
            price: finalPrice
          };
        }
        return item;
      });

      if (!found) {
        const fallbackItem = (initialProducts || []).find(p => String(p.id) === targetId) || { id: targetId, ...updatedFields };
        const merged = { ...fallbackItem, ...updatedFields };
        const isManual = merged.pricingMode === 'manual';
        const pricing = calculateProductPrice(merged, masterMetalRates);
        const finalPrice = (isManual && Number(merged.price) > 0) ? Number(merged.price) : pricing.finalPrice;
        nextProds = [{ ...merged, pricingMode: isManual ? 'manual' : 'auto', price: finalPrice }, ...nextProds];
      }

      try {
        localStorage.setItem('ml_products_v1', JSON.stringify(nextProds));
        localStorage.setItem('ml_products_vault_backup', JSON.stringify(nextProds));
      } catch (e) { console.warn(e); }
      return nextProds;
    });

    try {
      await api.saveProduct({ id, ...updatedFields });
    } catch (err) {
      console.warn('API update product deferred:', err);
    }
    addToast('Catalog item updated successfully!', 'success');
  };

  const deleteProduct = (id) => {
    const targetId = String(id);
    setProducts((prev) => {
      const updated = prev.filter((item) => String(item.id) !== targetId && String(item._id) !== targetId);
      try { localStorage.setItem('ml_products_v1', JSON.stringify(updated)); } catch (e) { console.warn(e); }
      return updated;
    });

    try {
      const deletedIds = JSON.parse(localStorage.getItem('ml_deleted_product_ids') || '[]');
      if (!deletedIds.includes(targetId)) {
        deletedIds.push(targetId);
        localStorage.setItem('ml_deleted_product_ids', JSON.stringify(deletedIds));
      }
    } catch (e) { console.warn(e); }

    api.deleteProduct(id);
    addToast('Item removed from catalog', 'info');
  };

  const toggleProductStock = (id) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, inStock: !item.inStock } : item
      )
    );
    api.toggleProductStock(id);
    addToast('Product stock status updated', 'info');
  };

  // Master Metal Rates Updater (Gold & Silver rates per gram + purities)
  const updateMasterMetalRates = async (newMasterRates) => {
    setMasterMetalRates(newMasterRates);
    try {
      localStorage.setItem('ml_master_metal_rates_v1', JSON.stringify(newMasterRates));
    } catch (e) {
      console.error(e);
    }

    // Extract default 24K, 22K, 18K, 999 for backward compatibility with live tickers
    const g24 = newMasterRates.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates.gold24K || 7380;
    const g22 = newMasterRates.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates.gold22K || 6765;
    const g18 = newMasterRates.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates.gold18K || 5535;
    const s999 = newMasterRates.silver?.find((s) => s.label?.includes('999') || s.id?.includes('999'))?.rate || liveGoldRates.silver999 || 89.5;

    const formattedTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const formattedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const timestamp = `Manual Rate (${formattedDate}, ${formattedTime})`;

    const updatedRates = {
      ...liveGoldRates,
      gold24K: Number(g24),
      gold24k: Number(g24),
      gold22K: Number(g22),
      gold22k: Number(g22),
      gold18K: Number(g18),
      gold18k: Number(g18),
      silver999: Number(s999),
      silver10g: Math.round(Number(s999) * 10),
      silver1kg: Math.round(Number(s999) * 1000),
      masterRates: newMasterRates,
      lastUpdated: timestamp
    };

    setLiveGoldRates(updatedRates);
    try {
      localStorage.setItem('ml_gold_rates_v1', JSON.stringify(updatedRates));
    } catch (e) {
      console.error(e);
    }

    const apiRes = await api.updateGoldRates(updatedRates);
    if (apiRes) {
      addToast('Manual metal rates saved to database & broadcast live!', 'success');
    } else {
      addToast('Manual metal rates updated across website vault!', 'success');
    }
    return updatedRates;
  };

  // Admin Gold Rate Updater
  const updateGoldRates = (newRates) => {
    const updated = {
      ...newRates,
      lastUpdated: 'Just Now (Admin Modified)'
    };
    if (newRates.masterRates) {
      setMasterMetalRates(newRates.masterRates);
      try {
        localStorage.setItem('ml_master_metal_rates_v1', JSON.stringify(newRates.masterRates));
      } catch (e) {
        console.error(e);
      }
    }
    setLiveGoldRates(updated);
    api.updateGoldRates(updated);
    addToast('Live Bullion rates updated across storefront!', 'success');
  };

  // Admin Order Operations
  const updateOrderStatus = (orderId, newStatus, courierData = {}) => {
    setOrders((prev) =>
      prev.map((ord) => (ord.id === orderId ? { ...ord, status: newStatus, ...courierData } : ord))
    );
    api.updateOrderStatus(orderId, newStatus, courierData);
    addToast(`Order ${orderId} marked as "${newStatus}"`, 'success');
  };

  const addNewOrder = (order) => {
    setOrders((prev) => [order, ...prev]);
    api.saveOrder(order);
  };

  const addManualOrder = (order) => {
    const newOrder = {
      id: `MLJ-${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      status: 'Processing',
      ...order
    };
    setOrders((prev) => [newOrder, ...prev]);
    api.saveOrder(newOrder);
    addToast(`Manual Walk-in Order #${newOrder.id} created successfully!`, 'success');
    return newOrder;
  };

  // Admin Coupons Operations
  const addCoupon = (newCoupon) => {
    const coupon = {
      id: `c-${Date.now()}`,
      isActive: true,
      ...newCoupon
    };
    setCoupons((prev) => {
      const updated = [coupon, ...prev];
      try { localStorage.setItem('ml_coupons_v1', JSON.stringify(updated)); } catch (e) { console.error(e); }
      return updated;
    });
    api.saveCoupon(coupon);
    addToast(`Coupon code ${coupon.code} activated!`, 'success');
  };

  const updateCoupon = (id, fields) => {
    setCoupons((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, ...fields } : c));
      try { localStorage.setItem('ml_coupons_v1', JSON.stringify(updated)); } catch (e) { console.error(e); }
      return updated;
    });
    api.saveCoupon({ id, ...fields });
    addToast('Coupon updated successfully!', 'success');
  };

  const deleteCoupon = (id) => {
    setCoupons((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      try { localStorage.setItem('ml_coupons_v1', JSON.stringify(updated)); } catch (e) { console.error(e); }
      return updated;
    });
    api.deleteCoupon(id);
    addToast('Coupon removed', 'info');
  };

  const toggleCouponStatus = (id) => {
    setCoupons((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, isActive: !c.isActive } : c));
      try { localStorage.setItem('ml_coupons_v1', JSON.stringify(updated)); } catch (e) { console.error(e); }
      return updated;
    });
    api.toggleCoupon(id);
    addToast('Coupon active status toggled', 'info');
  };

  // Dynamic Apply Coupon with validation against coupons list
  const applyCoupon = (code) => {
    const cleanCode = code.trim().toUpperCase();
    const found = coupons.find((c) => c.code.toUpperCase() === cleanCode);

    if (found) {
      if (!found.isActive) {
        setCouponMessage({ type: 'error', text: `Coupon ${cleanCode} is no longer active.` });
        addToast(`Coupon ${cleanCode} has expired or is inactive`, 'error');
        return;
      }
      if (found.minOrder && cartSubtotal < found.minOrder) {
        setCouponMessage({ 
          type: 'error', 
          text: `Minimum order of ₹${found.minOrder.toLocaleString('en-IN')} required for ${cleanCode}.` 
        });
        addToast(`Min order ₹${found.minOrder.toLocaleString('en-IN')} needed for this coupon`, 'error');
        return;
      }

      setAppliedDiscountPercent(found.discountPercent);
      setCouponCode(cleanCode);
      setCouponMessage({ 
        type: 'success', 
        text: `Privilege code ${cleanCode} applied! You save ${found.discountPercent}%.` 
      });
      addToast(`Promo ${cleanCode} applied (${found.discountPercent}% OFF)`, 'success');
    } else {
      if (cleanCode === 'ROYAL10') {
        setAppliedDiscountPercent(10);
        setCouponCode('ROYAL10');
        setCouponMessage({ type: 'success', text: 'Royal Privilege 10% OFF applied!' });
        addToast('Promo Code ROYAL10 applied (10% OFF)', 'success');
      } else if (cleanCode === 'GOLD5') {
        setAppliedDiscountPercent(5);
        setCouponCode('GOLD5');
        setCouponMessage({ type: 'success', text: '5% Festive Discount applied!' });
        addToast('Promo Code GOLD5 applied (5% OFF)', 'success');
      } else {
        setCouponMessage({ type: 'error', text: 'Invalid coupon code. Try "ROYAL10" or "KITCHEN15"' });
        addToast('Invalid coupon code', 'error');
      }
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedDiscountPercent(0);
    setCouponMessage(null);
    addToast('Coupon code removed', 'info');
  };

  // Inquiry / Lead Operations
  const updateInquiryStatus = (id, newStatus, notes = '') => {
    setConsultations((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: newStatus, ...(notes ? { notes } : {}) } : item
      )
    );
    api.updateInquiryStatus(id, newStatus, notes);
    addToast(`Inquiry #${id} updated to ${newStatus}`, 'success');
  };

  const addInquiry = (inquiryData) => {
    const newInq = {
      id: `REQ-${Math.floor(100 + Math.random() * 900)}`,
      date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      status: 'New Lead',
      ...inquiryData
    };
    setConsultations((prev) => [newInq, ...prev]);
    api.saveInquiry(newInq);
    addToast('New VIP Consultation request registered!', 'success');
    return newInq;
  };

  // Store Settings & Backup Operations
  const updateStoreSettings = (newFields) => {
    setStoreSettings((prev) => ({ ...prev, ...newFields }));
    api.updateSettings(newFields);
    addToast('Store configuration & tax settings updated!', 'success');
  };

  const exportStoreBackup = () => {
    const backupData = {
      exportedAt: new Date().toISOString(),
      storeName: storeSettings.storeName,
      version: '2.0.0',
      storeSettings,
      categories,
      products,
      orders,
      coupons,
      consultations,
      liveGoldRates,
      heroBannerData,
      announcementData,
      storyHighlightsData,
      testimonialsData,
      trustPillarsData,
      promoPopupData
    };

    const jsonStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(backupData, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', jsonStr);
    downloadAnchor.setAttribute('download', `ML_Jewellers_FullBackup_${new Date().toISOString().slice(0,10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    addToast('Full store & CMS backup exported as JSON file', 'success');
  };

  const importStoreBackup = (jsonData) => {
    try {
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      if (data.categories && Array.isArray(data.categories)) setCategories(data.categories);
      if (data.products && Array.isArray(data.products)) setProducts(data.products);
      if (data.orders && Array.isArray(data.orders)) setOrders(data.orders);
      if (data.coupons && Array.isArray(data.coupons)) setCoupons(data.coupons);
      if (data.consultations && Array.isArray(data.consultations)) setConsultations(data.consultations);
      if (data.liveGoldRates) setLiveGoldRates(data.liveGoldRates);
      if (data.storeSettings) setStoreSettings(data.storeSettings);
      if (data.heroBannerData) setHeroBannerData(data.heroBannerData);
      if (data.announcementData) setAnnouncementData(data.announcementData);
      if (data.storyHighlightsData) setStoryHighlightsData(data.storyHighlightsData);
      if (data.testimonialsData) setTestimonialsData(data.testimonialsData);
      if (data.trustPillarsData) setTrustPillarsData(data.trustPillarsData);
      if (data.promoPopupData) setPromoPopupData(data.promoPopupData);

      addToast('Backup restored successfully into live database!', 'success');
      return true;
    } catch (e) {
      console.error(e);
      addToast('Failed to parse backup file. Please check JSON format.', 'error');
      return false;
    }
  };

  const resetToDefaultData = () => {
    try {
      localStorage.removeItem('ml_deleted_product_ids');
      localStorage.removeItem('ml_deleted_category_ids');
      localStorage.setItem('ml_products_v1', JSON.stringify(initialProducts));
      localStorage.setItem('ml_products_vault_backup', JSON.stringify(initialProducts));
      localStorage.setItem('ml_categories_v1', JSON.stringify(defaultCategories));
      localStorage.setItem('ml_categories_vault_backup', JSON.stringify(defaultCategories));
    } catch (e) { console.error(e); }
    setProducts(initialProducts);
    setCategories(defaultCategories);

    setLiveGoldRates(defaultRates);
    setCoupons(defaultCoupons);
    setStoreSettings(defaultStoreSettings);
    setHeroBannerData(defaultHeroBanner);
    setAnnouncementData(defaultAnnouncements);
    setStoryHighlightsData(defaultStories);
    setTestimonialsData(defaultTestimonials);
    setTrustPillarsData(defaultTrustPillars);
    setPromoPopupData(defaultPromoPopup);

    localStorage.removeItem('ml_gold_rates_v1');
    localStorage.removeItem('ml_coupons_v1');
    localStorage.removeItem('ml_settings_v1');
    localStorage.removeItem('ml_inquiries_v1');
    localStorage.removeItem('ml_cms_hero_v1');
    localStorage.removeItem('ml_cms_announcements_v1');
    localStorage.removeItem('ml_cms_stories_v1');
    localStorage.removeItem('ml_cms_testimonials_v1');
    localStorage.removeItem('ml_cms_trust_v1');
    localStorage.removeItem('ml_cms_popup_v1');
    addToast('Site UI settings & Master catalog restored to original state!', 'info');
  };

  // Cart operations
  const addToCart = (product, qty = 1) => {
    if (!product) return;
    const prodId = product.id;
    setCart((prev) => {
      const existing = prev.find((item) => {
        const p = item?.product || item;
        return p?.id === prodId;
      });
      if (existing) {
        return prev.map((item) => {
          const p = item?.product || item;
          return p?.id === prodId
            ? { ...item, product: p, quantity: (Number(item.quantity) || 1) + qty }
            : item;
        });
      }
      return [...prev, { product, quantity: qty }];
    });
    addToast(`"${(product.name || 'Jewellery').slice(0, 26)}..." added to bag`);
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => {
      const p = item?.product || item;
      return p?.id !== productId;
    }));
    addToast('Item removed from bag', 'info');
  };

  const updateQuantity = (productId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) => {
        const p = item?.product || item;
        return p?.id === productId ? { ...item, product: p, quantity: newQty } : item;
      })
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  // Wishlist operations
  const toggleWishlist = (productId) => {
    setWishlist((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const isFavorited = safePrev.includes(productId);
      if (isFavorited) {
        addToast('Removed from your Wishlist', 'info');
        return safePrev.filter((id) => id !== productId);
      } else {
        addToast('Saved to your Royal Wishlist', 'success');
        return [...safePrev, productId];
      }
    });
  };

  const isInWishlist = (productId) => Array.isArray(wishlist) && wishlist.includes(productId);

  // Dynamic Products with automatic Master Rate pricing calculation
  // Formula:
  // Metal Value = Product Weight (g) * Current Master Rate Per Gram
  // Making Charge = Metal Value * (Making Charge % / 100)
  // Final Product Price = Metal Value + Making Charge
  const dynamicProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : (Array.isArray(initialProducts) ? initialProducts : []);
    return list.map((p) => {
      if (!p) return null;
      const pricing = calculateProductPrice(p, masterMetalRates) || {};
      return {
        ...p,
        price: pricing.finalPrice || Number(p.price) || 0,
        taxableAmount: pricing.taxableAmount || 0,
        gstAmount: pricing.gstAmount || 0,
        metalValue: pricing.metalValue || 0,
        diamondAmount: pricing.diamondAmount || 0,
        gemstoneAmount: pricing.gemstoneAmount || 0,
        makingCharge: pricing.makingCharge || 0,
        makingChargePercent: pricing.makingChargePercent || 0,
        ratePerGram: pricing.ratePerGram || 0,
        effectiveWeight: pricing.weight || 0
      };
    }).filter(Boolean);
  }, [products, masterMetalRates]);

  // Calculations
  const safeCart = Array.isArray(cart) ? cart : [];
  const cartCount = safeCart.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
  
  // Taxable subtotal (pre-tax base): cartSubtotal + gstAmount = cartTotal (Total Payable)
  let calculatedTaxableSubtotal = 0;
  safeCart.forEach((item) => {
    const prod = item?.product || item;
    if (!prod || typeof prod !== 'object') return;
    const livePricing = calculateProductPrice(prod, masterMetalRates);
    const unitTaxable = livePricing?.taxableAmount || Math.round((Number(prod.price) || 0) / 1.03);
    const qty = Number(item?.quantity) || 1;
    calculatedTaxableSubtotal += unitTaxable * qty;
  });

  const cartSubtotal = calculatedTaxableSubtotal;
  const discountAmount = Math.round((cartSubtotal * appliedDiscountPercent) / 100);
  const gstAmount = Math.round((cartSubtotal - discountAmount) * 0.03);
  
  // Customizable Courier / Shipping Fee
  const courierFeeSetting = Number(storeSettings?.courierCharge) || 0;
  const freeThresholdSetting = Number(storeSettings?.freeShippingThreshold) || 50000;
  const shippingFee = (cartSubtotal >= freeThresholdSetting || cartSubtotal === 0) ? 0 : courierFeeSetting;
  const cartTotal = (cartSubtotal - discountAmount) + gstAmount + shippingFee;

  // Filtered Products Logic (Evaluated on dynamically calculated prices)
  const safeDynamicProducts = Array.isArray(dynamicProducts) ? dynamicProducts : [];
  const searchLower = (searchQuery || '').toLowerCase();
  const filteredProducts = safeDynamicProducts.filter((item) => {
    if (!item) return false;
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch =
      (item.name || '').toLowerCase().includes(searchLower) ||
      (item.subtitle || '').toLowerCase().includes(searchLower) ||
      (item.metal || '').toLowerCase().includes(searchLower) ||
      (item.gemstone || '').toLowerCase().includes(searchLower);
    
    const matchesMetal =
      selectedMetal === 'all' ||
      item.metal?.toLowerCase().includes(selectedMetal.toLowerCase());

    const matchesPrice = item.price <= maxPrice;

    return matchesCategory && matchesSearch && matchesMetal && matchesPrice;
  }).sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'rating') return b.rating - a.rating;
    return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
  });

  return (
    <ShopContext.Provider
      value={{
        isAdminMode,
        setIsAdminMode,
        isAdminAuthenticated,
        setIsAdminAuthenticated,
        loginAdmin,
        logoutAdmin,
        storeSettings,
        updateStoreSettings,
        exportStoreBackup,
        importStoreBackup,
        resetToDefaultData,
        categories,
        addCategory,
        updateCategory,
        deleteCategory,
        resetCategoriesToDefault,
        restoreMasterCatalog,
        products: dynamicProducts,
        rawProducts: products,
        filteredProducts,
        addProduct,
        updateProduct,
        deleteProduct,
        toggleProductStock,
        masterMetalRates,
        updateMasterMetalRates,
        calculateProductPrice,
        getMasterRateForProduct,
        liveGoldRates,
        updateGoldRates,
        coupons,
        addCoupon,
        updateCoupon,
        deleteCoupon,
        toggleCouponStatus,
        orders,
        updateOrderStatus,
        addNewOrder,
        addManualOrder,
        consultations,
        updateInquiryStatus,
        addInquiry,
        invoiceModalOrder,
        setInvoiceModalOrder,
        // CMS State & Modifiers
        heroBannerData,
        updateHeroBanner,
        announcementData,
        updateAnnouncements,
        storyHighlightsData,
        addStory,
        updateStory,
        deleteStory,
        testimonialsData,
        addTestimonial,
        updateTestimonial,
        deleteTestimonial,
        trustPillarsData,
        updateTrustPillars,
        promoPopupData,
        updatePromoPopup,
        isPromoPopupDismissed,
        setIsPromoPopupDismissed,
        // Live Bullion Rate Engine & Marketing Banner
        rateApiSettings,
        updateRateApiSettings,
        marketingOfferData,
        updateMarketingOfferData,
        rateHistoryData,
        autoRefreshCountdown,
        isRefreshingRates,
        refreshLiveRates,
        // Storefront Cart & Filters
        cart,
        cartCount,
        cartSubtotal,
        discountAmount,
        gstAmount,
        cartTotal,
        shippingFee,
        freeShippingThreshold: freeThresholdSetting,
        courierCharge: courierFeeSetting,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        wishlist,
        toggleWishlist,
        isInWishlist,
        isCartOpen,
        setIsCartOpen,
        isWishlistOpen,
        setIsWishlistOpen,
        quickViewProduct,
        setQuickViewProduct,
        isCheckoutOpen,
        setIsCheckoutOpen,
        policyModal,
        setPolicyModal,
        openPolicyModal,
        closePolicyModal,
        activeCategory,
        setActiveCategory,
        searchQuery,
        setSearchQuery,
        selectedMetal,
        setSelectedMetal,
        maxPrice,
        setMaxPrice,
        sortBy,
        setSortBy,
        couponCode,
        couponMessage,
        appliedDiscountPercent,
        applyCoupon,
        removeCoupon,
        toasts,
        addToast,
        // JewelCasa B2B & Digital Platform Features
        isWastageModalOpen,
        setIsWastageModalOpen,
        isWastageListOpen,
        setIsWastageListOpen,
        isB2BClubModalOpen,
        setIsB2BClubModalOpen,
        isChatWidgetOpen,
        setIsChatWidgetOpen,
        b2bMember,
        setB2bMember: saveB2BMember,
        logoutB2B,
        // Customer Account & Dashboard
        currentCustomer,
        isCustomerLoggedIn,
        isCustomerAuthModalOpen,
        customerAuthMode,
        openCustomerAuthModal,
        closeCustomerAuthModal,
        signupCustomer,
        loginCustomer,
        loginCustomerWithOtp,
        logoutCustomer,
        updateCustomerProfile,
        savedAddresses,
        addSavedAddress,
        deleteSavedAddress,
        // MERN Database Status & Synchronization
        dbStatus,
        isSyncingDb,
        syncDatabase
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};
